import { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm, type Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Alert, Box, Button, Link, Stack, TextField, Typography } from '@mui/material';
import Grid from '@mui/material/Grid';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useSearchParams } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import * as yup from 'yup';
import { PLAN_CONFIG, resolvePlanFromParam } from 'data/plan-config';
import PasswordTextField from 'components/common/PasswordTextField';
import TurnstileWidget, { TurnstileWidgetHandle } from 'components/common/TurnstileWidget';
import { applyApiDetailsToForm } from 'utils/apiDetailsToFormErrors';
import { translateApiMessage } from 'utils/translateApiMessage';
import { getPrivacyPolicyUrl, getTermsUrl } from 'utils/landingUrls';
import { trackSignupStarted } from 'lib/analytics';

interface CreateEventFormProps {
  // Fixed by the route — each event type has its own create-event URL, so the
  // form no longer renders an in-form type toggle.
  eventType: CreateEventType;
  handleCreateEvent: (data: CreateEventFormValues) => any;
  loginLink: string;
}

export type CreateEventType = 'wedding' | 'baptism' | 'wedding_baptism';

export interface CreateEventFormValues {
  email: string;
  password: string;
  eventType: CreateEventType;
  partnerName1: string;
  partnerName2: string;
  babyName?: string;
  eventDate: string;
  turnstileToken?: string;
}

// Module-scoped so a single signup intent yields a single `signup_started`
// event across React StrictMode's double-invoke, real component remounts, and
// toggling between the three create-event routes (wedding / baptism /
// wedding_baptism) — all of which share this one component. A `useRef` would
// reset on a genuine remount; a module flag survives for the page-load lifetime,
// which is the once-per-intent window we want.
let signupStartedFired = false;

const CreateEventForm = ({ eventType, handleCreateEvent, loginLink }: CreateEventFormProps) => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const selectedPlan = resolvePlanFromParam(searchParams.get('plan'));
  const isPaidPlan = selectedPlan !== 'free';
  const planDisplayName = PLAN_CONFIG[selectedPlan].displayName;
  // Arrivals from the landing-page Éros CTA carry `?ref=eros` (always with
  // plan=heirloom → 'pro'). Show an Éros-flavored subtitle only for those, so
  // the generic "Choose Heirloom" flow keeps its neutral copy.
  const fromEros = searchParams.get('ref') === 'eros' && selectedPlan === 'pro';

  // signup_started (Browser-owned INTENT) — the canonical UI location per
  // docs/analytics/authentication-lifecycle.md §1. Fired once when the
  // create-event form is first seen; the module-scoped guard collapses
  // StrictMode/remounts/route-toggles to a single emission. The authoritative
  // signup_completed / email_verified / login milestones are Backend-owned and
  // are NOT emitted from the browser (see docs/analytics/event-ownership.md).
  useEffect(() => {
    if (signupStartedFired) return;
    signupStartedFired = true;
    trackSignupStarted({
      eventType,
      plan: selectedPlan,
      ref: searchParams.get('ref') ?? undefined,
    });
    // Fire once on first mount; the module-scoped guard covers remounts.
  }, []);

  const turnstileSiteKey = (import.meta.env.VITE_TURNSTILE_SITE_KEY || '').trim();
  const turnstileRef = useRef<TurnstileWidgetHandle>(null);

  const termsUrl = getTermsUrl();
  const privacyUrl = getPrivacyPolicyUrl();

  const schema = useMemo(
    () =>
      yup
        .object({
          email: yup
            .string()
            .email(t('validation.email_invalid'))
            .required(t('validation.required')),
          password: yup
            .string()
            .min(8, t('validation.password_min_length', { min: 8 }))
            .required(t('validation.required')),
          eventType: yup
            .mixed<CreateEventType>()
            .oneOf(['wedding', 'baptism', 'wedding_baptism'])
            .required(t('validation.required')),
          partnerName1: yup.string().required(t('validation.required')),
          // Second person is the partner (wedding) or the other parent
          // (baptism). Still required so the celebration is always attributed to
          // two named people; for a single-parent baptism the field can hold the
          // same first name twice — kept simple for the trial.
          partnerName2: yup.string().required(t('validation.required')),
          // Baby name only matters (and is required) for baptism flows.
          babyName: yup.string().when('eventType', {
            is: (v: CreateEventType) => v === 'baptism' || v === 'wedding_baptism',
            then: (s) => s.required(t('validation.required')),
            otherwise: (s) => s.optional(),
          }),
          eventDate: yup.string().required(t('validation.required')),
          turnstileToken: yup.string().optional(),
        })
        .required(),
    [t],
  );

  const {
    register,
    handleSubmit,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<CreateEventFormValues>({
    // Cast: the yup `.when()` on babyName makes its inferred type
    // `string | undefined` (required-when-baptism), which the resolver's
    // generics don't reconcile with the optional `babyName?` on the exported
    // interface. The runtime validation is correct; this only aligns types.
    resolver: yupResolver(schema) as Resolver<CreateEventFormValues>,
    // Seeded from the route-fixed prop; no in-form control changes it. Kept in
    // the form values so the yup `babyName.when('eventType')` rule resolves.
    defaultValues: { eventType },
  });

  const isBaptism = eventType === 'baptism';
  const showBaby = eventType === 'baptism' || eventType === 'wedding_baptism';

  // Labels shift with the event type. Baptism reframes the two people as
  // parents; combined/wedding keep the partner framing. Date label tracks type.
  const personOneLabel = isBaptism
    ? t('auth.create_event.parent_name_label')
    : t('auth.create_event.first_name_label');
  const personOnePlaceholder = isBaptism
    ? t('auth.create_event.parent_name_placeholder')
    : t('auth.create_event.first_name_placeholder');
  const personTwoLabel = isBaptism
    ? t('auth.create_event.parent_two_name_label')
    : t('auth.create_event.partner_name_label');
  const personTwoPlaceholder = isBaptism
    ? t('auth.create_event.parent_two_name_placeholder')
    : t('auth.create_event.partner_name_placeholder');
  const dateLabel = isBaptism
    ? t('auth.create_event.baptism_date_label')
    : eventType === 'wedding_baptism'
      ? t('auth.create_event.event_date_label')
      : t('auth.create_event.wedding_date_label');

  const onSubmit = async (data: CreateEventFormValues) => {
    try {
      let turnstileToken: string | undefined;
      if (turnstileSiteKey && turnstileRef.current) {
        turnstileToken = await turnstileRef.current.execute();
      }
      const payload: CreateEventFormValues = {
        ...data,
        ...(turnstileToken ? { turnstileToken } : {}),
      };
      await handleCreateEvent(payload);
    } catch (error: unknown) {
      if (!error) return;
      const handled = applyApiDetailsToForm<CreateEventFormValues>(error, setError, t);
      if (!handled) {
        setError('root.credential', {
          type: 'manual',
          message: translateApiMessage(error, t, {
            fallback: t('auth.create_event.error_default'),
          }),
        });
      }
    }
  };

  return (
    <Stack
      direction="column"
      sx={{
        minHeight: 1,
        alignItems: 'center',
        justifyContent: { xs: 'flex-start', lg: 'center' },
        pt: { xs: 4, lg: 10 },
        pb: 10,
      }}
    >
      <Grid
        container
        sx={{
          maxWidth: '35rem',
          rowGap: 4,
          p: { xs: 3, sm: 5 },
        }}
      >
        <Grid size={12}>
          <Stack
            direction={{ xs: 'column', sm: 'column' }}
            spacing={1}
            sx={{
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'flex-start' },
            }}
          >
            {isPaidPlan && (
              <Typography variant="overline" sx={{ color: 'text.secondary', letterSpacing: 1.5 }}>
                {t('auth.create_event.plan_chosen', { planName: planDisplayName })}
              </Typography>
            )}
            <Typography variant="h4">{t('auth.create_event.title')}</Typography>
            <Typography variant="body1">
              {t(fromEros ? 'auth.create_event.subtitle_eros' : 'auth.create_event.subtitle')}
            </Typography>
          </Stack>
        </Grid>

        <Grid size={12}>
          <Box component="form" noValidate onSubmit={handleSubmit(onSubmit)}>
            {errors.root?.credential?.message && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errors.root?.credential?.message}
              </Alert>
            )}
            <Grid container spacing={3}>
              <Grid size={12} sx={{ pb: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary', display: 'block', mb: -1.5 }}
                >
                  {t('auth.create_event.section_account')}
                </Typography>
              </Grid>

              <Grid size={12}>
                <TextField
                  fullWidth
                  size="large"
                  id="email"
                  type="email"
                  label={t('auth.create_event.email_label')}
                  variant="filled"
                  error={!!errors.email}
                  helperText={<>{errors.email?.message}</>}
                  {...register('email')}
                />
              </Grid>

              <Grid size={12}>
                <PasswordTextField
                  fullWidth
                  size="large"
                  id="password"
                  label={t('auth.create_event.password_label')}
                  variant="filled"
                  error={!!errors.password}
                  helperText={<>{errors.password?.message}</>}
                  {...register('password')}
                />
              </Grid>

              <Grid size={12} sx={{ pb: 0 }}>
                <Typography
                  variant="subtitle1"
                  sx={{ color: 'text.secondary', display: 'block', mt: 1.5, mb: -1.5 }}
                >
                  {t('auth.create_event.section_celebration')}
                </Typography>
              </Grid>

              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="large"
                  id="partnerName1"
                  type="text"
                  label={personOneLabel}
                  placeholder={personOnePlaceholder}
                  variant="filled"
                  error={!!errors.partnerName1}
                  helperText={<>{errors.partnerName1?.message}</>}
                  {...register('partnerName1')}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  size="large"
                  id="partnerName2"
                  type="text"
                  label={personTwoLabel}
                  placeholder={personTwoPlaceholder}
                  variant="filled"
                  error={!!errors.partnerName2}
                  helperText={<>{errors.partnerName2?.message}</>}
                  {...register('partnerName2')}
                />
              </Grid>

              {showBaby && (
                <Grid size={12}>
                  <TextField
                    fullWidth
                    size="large"
                    id="babyName"
                    type="text"
                    label={t('auth.create_event.baby_name_label')}
                    placeholder={t('auth.create_event.baby_name_placeholder')}
                    variant="filled"
                    error={!!errors.babyName}
                    helperText={<>{errors.babyName?.message}</>}
                    {...register('babyName')}
                  />
                </Grid>
              )}

              <Grid size={12}>
                <Controller
                  name="eventDate"
                  control={control}
                  render={({ field }) => (
                    <DatePicker
                      label={dateLabel}
                      format="DD/MM/YYYY"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(date: Dayjs | null) => {
                        field.onChange(date ? date.format('YYYY-MM-DD') : '');
                      }}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          size: 'large' as any,
                          variant: 'filled',
                          error: !!errors.eventDate,
                          helperText: <>{errors.eventDate?.message}</>,
                        },
                      }}
                    />
                  )}
                />
              </Grid>

              {turnstileSiteKey && (
                <Grid size={12}>
                  <Stack direction="column" alignItems="center">
                    <TurnstileWidget ref={turnstileRef} siteKey={turnstileSiteKey} mode="execute" />
                  </Stack>
                </Grid>
              )}

              <Grid size={12} sx={{ mt: 2 }}>
                <Button
                  loading={isSubmitting}
                  fullWidth
                  type="submit"
                  size="large"
                  variant="contained"
                  color="neutral"
                  sx={{ py: '14px' }}
                >
                  {t('auth.create_event.submit')}
                </Button>
              </Grid>
              <Grid size={12}>
                <Typography
                  variant="body2"
                  sx={{ color: 'text.secondary', textAlign: 'center', px: 3 }}
                >
                  {t('auth.create_event.terms_intro')}{' '}
                  <Link href={termsUrl} target="_blank" rel="noopener noreferrer">
                    {t('auth.create_event.terms_link')}
                  </Link>{' '}
                  &{' '}
                  <Link href={privacyUrl} target="_blank" rel="noopener noreferrer">
                    {t('auth.create_event.privacy_link')}
                  </Link>
                  .
                </Typography>
              </Grid>
              <Grid size={12} sx={{ mt: 0 }}>
                <Stack direction="row" justifyContent="center">
                  <Typography variant="subtitle2" sx={{ color: 'text.secondary' }}>
                    {t('auth.create_event.existing_user_prefix')}
                    <Link href={loginLink} sx={{ ml: 1 }}>
                      {t('auth.create_event.login_link')}
                    </Link>
                  </Typography>
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Grid>
      </Grid>
    </Stack>
  );
};

export default CreateEventForm;
